import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import PalmUploader from "../components/PalmUploader";
import MessageRenderer from "../components/chat/messages/MessageRenderer";
import WalletTopUp from "../components/WalletTopUp";
import VipKingOffer from "../components/VipKingOffer";
import VipKingBadge from "../components/VipKingBadge";
import CashbackCelebrationModal from "../components/CashbackCelebrationModal";
import PostChatNotificationPrompt, {
  incrementChatEndedCounter,
} from "../components/PostChatNotificationPrompt";
import api, { resolveAssetUrl } from "../lib/api";
import { hapticMedium, hapticError } from "../lib/nativeBridge";
import { unlockAudio } from "../lib/chatSounds";
import { getAdSessionId } from "../lib/adAttribution";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useOngoingChat } from "../context/OngoingChatContext";
import { useChatBilling } from "../hooks/useChatBilling";
import { useChatMessages } from "../hooks/useChatMessages";
import { PaperPlaneTilt, Star, ArrowLeft, X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ChatPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user, loading, checkAuth } = useAuth();
  const { formatPrice } = useCurrency();
  const { registerActiveChat, clearActiveChat, markRead } = useOngoingChat();
  const [chat, setChat] = useState(null);
  const [astro, setAstro] = useState(null);
  const [text, setText] = useState("");
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupShortBy, setTopupShortBy] = useState(0);
  // When set, the WalletTopUp modal will auto-apply this coupon code
  // (e.g. "ANNIVERSARY50" from the post-chat Anniversary banner CTA).
  const [topupPrefillCoupon, setTopupPrefillCoupon] = useState(null);
  // Global Anniversary promo kill-switch — fetched from /api/promos/anniversary.
  // When false, the golden banner inside the chat is suppressed entirely;
  // the chat looks like a normal chat with no promo ribbon. Super-admin
  // controls this from /admin/promos. Defaults to true so the banner stays
  // visible during the brief fetch window (no FOUC hide-then-show flicker).
  const [anniversaryPromoOn, setAnniversaryPromoOn] = useState(true);
  // For HUMAN astrologer chats, the Anniversary banner only shows to users
  // who have NEVER completed a Razorpay/PayPal wallet recharge — it's a
  // first-time-paid acquisition nudge. Users with only free promotional
  // credit still qualify. AI astrologer chats ignore this flag entirely.
  // Defaults to null until the API resolves, treated as "hasn't paid yet".
  const [hasPaidRecharge, setHasPaidRecharge] = useState(null);
  // True when the top-up modal was auto-opened because the user has no
  // affordable time left — the WalletTopUp copy below switches headline
  // and subline to a "recharge to start chatting" framing in that case.
  const [topupForZeroBalance, setTopupForZeroBalance] = useState(false);
  const [showVipOffer, setShowVipOffer] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  // First-chat cashback celebration: surfaced ONCE per chat-end if the
  // server stamped the seeker as newly-eligible (or the user already had
  // unredeemed cashback waiting from a prior end-event).
  const [cashbackCelebration, setCashbackCelebration] = useState(null);
  // Whose-chart picker — shown on first open of any AI-astrologer chat until
  // the seeker confirms whether they want a reading about themselves or
  // someone else. Once dismissed, never re-shown for the same chat.
  const [showChartPicker, setShowChartPicker] = useState(false);
  const [showExternalForm, setShowExternalForm] = useState(false);
  const [extForm, setExtForm] = useState({ name: "", date: "", time: "", place: "", timezone: "" });
  const [extPlaceSuggestions, setExtPlaceSuggestions] = useState([]);
  const [extSubmitting, setExtSubmitting] = useState(false);
  const scrollRef = useRef(null);
  // One-shot guard: once we've toasted the "last 60 seconds" warning,
  // never spam the seeker again during the same chat session — even if
  // they keep sending messages in that window.
  const lastMinuteWarnedRef = useRef(false);

  // Messages, typing indicator and read receipts all live in this hook.
  const {
    messages,
    isTyping,
    peerTyping,
    pingTyping,
    readStatus,
    appendSentMessage,
    replaceAllMessages,
    expectReply,
  } = useChatMessages({
    chatId,
    chat,
    scrollRef,
    onIncomingMessage: markRead,
    // Status change (e.g. astrologer ended the chat) → propagate within
    // ~2s instead of waiting for the 15s billing heartbeat. The hook
    // already fetched the fresh chat + billing snapshots.
    onChatStatusChange: (refreshedChat, freshBilling) => {
      setChat(refreshedChat);
      if (freshBilling) setBilling({ ...freshBilling, _heartbeat_ts: Date.now() });
      // Status flipped to ended (astrologer-end, auto-end, decline) — tear
      // down the global OngoingChatDock immediately so the "LIVE WITH X"
      // dock doesn't keep mis-directing the seeker to a stale chat.
      if (refreshedChat?.status === "ended") {
        clearActiveChat();
      }
      if (refreshedChat?.status === "ended" && refreshedChat?.ended_by === "astrologer") {
        toast(`${astro?.name?.split(" ")[0] || "The astrologer"} ended the consultation.`, { icon: "📜", duration: 5000 });
      }
      // Astrologer/auto-end path: surface the cashback celebration if the
      // server stamped this seeker as eligible (the field is on the
      // refreshed chat doc thanks to GET /chats/{id} hydration).
      if (
        refreshedChat?.status === "ended"
        && refreshedChat?.cashback_eligible
        && !cashbackCelebration
      ) {
        setCashbackCelebration({
          amount: refreshedChat.cashback_amount || 50,
        });
      }
    },
  });

  // Billing clock, heartbeat polling, start chime, countdown ticks and the
  // low-balance prompt all live in this hook.
  const {
    billing,
    setBilling,
    tick,
    lowBalancePromptAmount,
    clearLowBalancePrompt,
  } = useChatBilling(chatId, chat, astro?.rate_per_min, {
    onAutoEnd: () => {
      clearActiveChat();
      checkAuth?.();
    },
  });

  // Lift the low-balance prompt into the wallet-top-up modal whenever the
  // billing hook asks us to.
  useEffect(() => {
    if (lowBalancePromptAmount != null && !topupOpen) {
      setTopupShortBy(lowBalancePromptAmount);
      setTopupForZeroBalance(true);
      setTopupOpen(true);
      clearLowBalancePrompt();
    }
  }, [lowBalancePromptAmount, topupOpen, clearLowBalancePrompt]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    markRead();
    (async () => {
      const { data: chatData } = await api.get(`/chats/${chatId}`);
      setChat(chatData);
      // Register as the ongoing chat so the persistent dock surfaces when
      // the user navigates away. ALSO register while the chat is `ringing`
      // (post-resume, waiting for astrologer to pick up) — the dock then
      // shows a "Connecting…" pill so the user knows the request is still
      // alive in the background. Only `ended` clears the dock.
      if (chatData?.status === "active" || chatData?.status === "ringing") {
        registerActiveChat(chatData);
      } else {
        clearActiveChat();
      }
      try {
        const { data: astroData } = await api.get(`/astrologers/${chatData.astrologer_id}`);
        setAstro(astroData.astrologer);
      } catch (err) { console.error("Failed to load astrologer", err); }
    })();
  }, [chatId, user, loading, navigate, registerActiveChat, clearActiveChat, markRead]);

  // Fetch the Anniversary promo kill-switch once on mount. The endpoint
  // is public (no auth) so this runs even before chat hydration. If the
  // request fails (offline / 5xx) we keep the default `true` so the
  // marketing campaign isn't silently suppressed by a transient error.
  useEffect(() => {
    api.get("/promos/anniversary")
      .then(({ data }) => setAnniversaryPromoOn(data?.enabled !== false))
      .catch(() => { /* keep default ON */ });
  }, []);

  // Fetch whether the signed-in user has ever paid for a wallet recharge.
  // Drives the HUMAN-astrologer-only banner gating below. Requires auth so
  // we wait until the user object resolves. On failure default to false
  // (banner stays visible) so we don't silently kill the campaign.
  useEffect(() => {
    if (!user) return;
    api.get("/users/me/has-paid-recharge")
      .then(({ data }) => setHasPaidRecharge(!!data?.has_paid_recharge))
      .catch(() => setHasPaidRecharge(false));
  }, [user]);

  // If the chat is in 'ringing' state (human astrologer hasn't picked
  // up yet), poll every 3s until they accept or decline. On accept the
  // status flips to 'active' and the timer + billing start; on decline
  // status becomes 'ended' with declined=true.
  useEffect(() => {
    if (!chat || chat.status !== "ringing") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/chats/${chatId}`);
        if (cancelled) return;
        if (data.status === "active") {
          setChat(data);
          registerActiveChat(data);
          return;
        }
        if (data.status === "ended") {
          setChat(data);
          clearActiveChat();
          return;
        }
      } catch { /* keep polling */ }
      if (!cancelled) setTimeout(tick, 3000);
    };
    setTimeout(tick, 3000);
    return () => { cancelled = true; };
  }, [chat, chatId, registerActiveChat, clearActiveChat]);

  // Show the "whose-chart" picker EVERY time the chat page mounts for an
  // AI astrologer — on a brand-new chat, on resume of an ended chat, on a
  // return visit from "My chats", and even when external_chart is already
  // set (in that case "My own chart" will switch back). The seeker should
  // always have the option to pick whose chart to read at the start of a
  // session.
  // Suppression rules: chat ended, astrologer is not AI, OR astrologer
  // is a tarot reader (they pull cards based on the question instead of
  // anchoring to a kundali).
  useEffect(() => {
    if (!chat || !astro) return;
    if (chat.status === "ended") return;
    if (!astro.is_ai) return;
    if ((astro.specialization || "").toLowerCase() === "tarot") return;
    // Defer slightly so the chime + greeting render first
    const t = setTimeout(() => setShowChartPicker(true), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.chat_id, astro?.astrologer_id, astro?.specialization]);

  // "My own chart" handler — if the seeker previously had an external chart
  // set on this chat, auto-clear it so the AI switches back to their own
  // context. Otherwise just dismisses the picker.
  const pickMyOwnChart = async () => {
    if (chat?.external_chart?.kundali) {
      try {
        await api.post(`/chats/${chatId}/external-chart/clear`);
        const [{ data: refreshed }, { data: msgs }] = await Promise.all([
          api.get(`/chats/${chatId}`),
          api.get(`/chats/${chatId}/messages`),
        ]);
        setChat(refreshed);
        replaceAllMessages(msgs);
        toast.success("Back to your own chart");
      } catch {
        toast.error("Could not switch back");
      }
    } else {
      toast.success("Reading your own chart");
    }
    setShowChartPicker(false);
  };

  const submitExternalChart = async () => {
    if (extSubmitting) return;
    const name = extForm.name.trim();
    const date = extForm.date;
    const time = extForm.time;
    const place = extForm.place.trim();
    if (!name) { toast.error("Please enter the person's name."); return; }
    if (!date || !time || !place) {
      toast.error("Date, time and place are all required.");
      return;
    }
    if (!extForm.timezone) {
      toast.error("Pick the place from the dropdown so we have an accurate timezone.");
      return;
    }
    setExtSubmitting(true);
    try {
      await api.post(`/chats/${chatId}/external-chart`, {
        name, date, time, place, timezone: extForm.timezone,
      });
      const [{ data: refreshed }, { data: msgs }] = await Promise.all([
        api.get(`/chats/${chatId}`),
        api.get(`/chats/${chatId}/messages`),
      ]);
      setChat(refreshed);
      replaceAllMessages(msgs);
      setShowExternalForm(false);
      setShowChartPicker(false);
      toast.success(`Now reading ${name}'s chart`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute the chart.");
    } finally {
      setExtSubmitting(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || sending) return;
    // Haptic tap on the primary CTA — turns the pure-WebView shell into
    // a "hybrid app with native feedback" for Apple review Guideline 4.2.
    hapticMedium();
    // First user gesture in this session — guarantees the audio context is
    // unlocked, so the countdown ticks will play even if autoplay was blocked.
    try { unlockAudio(); } catch (_) {}
    const t = text.trim();
    setText("");
    setSending(true);
    try {
      const { data: msg } = await api.post(`/chats/${chatId}/messages`, { text: t });
      appendSentMessage(msg);
      // Trigger "typing..." indicator since an AI/offline-human reply is on the way.
      // AI guides ALWAYS reply; offline humans get an auto-reply too. Show indicator
      // optimistically and let the message-poll hook clear it when the reply arrives.
      const expectsReply = !!astro && (astro.is_ai || astro.is_online === false);
      if (expectsReply) {
        expectReply();
      }
      // Update local billing state from response
      if (msg.billing) {
        setBilling((prev) => ({
          ...(prev || {}),
          // First message just landed — clock is now ticking. Optimistically
          // flip billing_started so the "Paused" pill flips to "Time left"
          // immediately, without waiting for the next 15s heartbeat.
          billing_started: true,
          billing_started_at: prev?.billing_started_at || msg.created_at,
          _heartbeat_ts: Date.now(),
          elapsed_seconds: msg.billing.elapsed_seconds,
          elapsed_minutes: msg.billing.elapsed_minutes,
          free_minutes_remaining: msg.billing.free_minutes_remaining,
          rate_per_min: msg.billing.rate_per_min,
          wallet_balance: msg.billing.new_balance,
          total_charged: ((prev?.total_charged || 0) + (msg.billing.charged || 0)),
          billed_minutes: ((prev?.billed_minutes || 0) + (msg.billing.minutes_charged || 0)),
        }));
        // Pull authoritative billing state from the server immediately so
        // `seconds_left`, `affordable_paid_minutes`, etc. reflect the live
        // post-stamp values (don't wait the 15-second heartbeat interval).
        api.get(`/chats/${chatId}/billing`)
          .then(({ data }) => setBilling({ ...data, _heartbeat_ts: Date.now() }))
          .catch(() => {});
        // Per-minute billing toasts used to fire on every charged message
        // — irritating for active conversations. We now NEVER toast a
        // "₹X for Y min" pulse OR a "free minutes ending" cue. Only
        // surface a single "Last 60s" warning when the wallet is one
        // minute away from empty.
        // LAST-MINUTE warning — fires ONCE per chat when the remaining
        // wallet ≤ rate_per_min (≈60s of conversation left). Ref-gated so
        // a spammy 2nd send within that window doesn't re-toast.
        const ratePerMin = msg.billing.rate_per_min || 0;
        const bal = msg.billing.new_balance || 0;
        if (
          ratePerMin > 0
          && bal > 0
          && bal <= ratePerMin
          && !lastMinuteWarnedRef.current
        ) {
          lastMinuteWarnedRef.current = true;
          toast(
            `Last minute · only ₹${bal.toFixed(0)} left in wallet. Top up to keep the conversation going.`,
            { icon: "⚠️", duration: 7000 },
          );
        }
      }
      checkAuth?.();
    } catch (err) {
      setText(t);
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402) {
        toast.error(detail || "Insufficient balance. Please top up.", { duration: 8000 });
        // Extract shortfall from detail message to pre-suggest amount
        const m = /short by ₹?([\d.]+)/i.exec(detail || "");
        const short = m ? Math.ceil(parseFloat(m[1]) / 50) * 50 : 100;
        setTopupShortBy(short);
        setTopupOpen(true);
      } else if (status === 400) {
        toast.error(detail || "Could not send message.");
      } else {
        toast.error("Failed to send");
      }
    } finally {
      setSending(false);
    }
  };

  // Derived live elapsed values (refreshed every second via `tick`)
  // When the chat has ENDED, freeze at the server-stamped ended_at so the
  // displayed elapsed/timer don't keep ticking on the user's screen.
  const liveElapsed = (() => {
    // Billing has NOT started until customer sends first message.
    // Until then, show 00:00 / 0 minutes so the user understands the clock
    // is dormant. The server is the source of truth via billing_started_at.
    if (billing && billing.billing_started === false) {
      return { seconds: 0, minutes_ceil: 0, label: "00:00", not_started: true };
    }
    const anchor = billing?.billing_started_at || chat?.billing_started_at;
    if (!anchor) return null;
    const start = new Date(anchor);
    const isEnded = chat?.status === "ended" || billing?.status === "ended";
    // If chat ended, freeze elapsed at ended_at (fallback: server-reported
    // billable seconds from heartbeat). NEVER use Date.now() once ended.
    let endRef;
    if (isEnded) {
      if (chat?.ended_at) {
        endRef = new Date(chat.ended_at).getTime();
      } else if (billing?._heartbeat_ts) {
        endRef = billing._heartbeat_ts;
      } else {
        endRef = start.getTime();
      }
    } else {
      endRef = Date.now();
    }
    const secs = Math.max(0, Math.floor((endRef - start.getTime()) / 1000));
    const mm = Math.floor(secs / 60);
    const ss = secs % 60;
    return {
      seconds: secs,
      minutes_ceil: Math.ceil(secs / 60),
      label: `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`,
      frozen: isEnded,
    };
  })();
  void tick;  // referenced for re-render dependency

  const endChat = async () => {
    let cashback = null;
    try {
      const { data } = await api.post(`/chats/${chatId}/end`);
      if (data?.final_charge > 0) {
        toast.success(
          `Chat ended · Final ${data.final_minutes_charged} min settled for ₹${data.final_charge}`,
        );
      }
      cashback = data?.cashback || null;
    } catch (_) { /* ignore */ }
    // Clear from the ongoing-chat dock since this chat is explicitly over
    clearActiveChat();
    checkAuth?.();
    // Update local chat state so the green cashback banner + "Chat ended"
    // continuity marker + resume CTA render without waiting for the next
    // /chats/:id poll.
    setChat((prev) => prev ? { ...prev,
      status: "ended",
      cashback_eligible: cashback?.eligible || prev.cashback_eligible,
      cashback_amount: cashback?.amount || prev.cashback_amount || 50,
    } : prev);
    // Track chat-end count so downstream push prompts can fire on the
    // first exit-per-device (needed by the second-tap exitSession below).
    incrementChatEndedCounter();
    // ── UX rule (Mar 2026 rework) ──────────────────────────────────────
    // First X tap = end the session only. Do NOT auto-open the review
    // modal or the cashback celebration modal — both hid the green
    // "Congratulations! You won 50% Cashback · CLAIM NOW" banner that
    // renders in-place the moment `chat.status === "ended"`. Seekers
    // reported they couldn't claim the cashback because the review
    // modal was on top; several rated 1★ out of frustration.
    //
    // Second X tap (after chat is already ended) is handled by
    // `exitSession` below, which THEN opens the review modal + navigates.
    // ────────────────────────────────────────────────────────────────
  };

  // Second-tap-X handler — only fires when the chat is already ended.
  // Shows the "How was your reading?" modal (if the seeker had a real
  // session), then routes to /astrologers. Also fires directly when
  // there's nothing to rate so we never trap the user on a dead page.
  const exitSession = () => {
    const sentSomething = messages.some((m) => m.sender_role === "customer");
    const wasBilled = (billing?.billed_minutes ?? 0) > 0 || (billing?.total_charged ?? 0) > 0;
    if (sentSomething || wasBilled) {
      setShowRating(true);
    } else {
      finalizeChatEnd();
    }
  };

  // Final step of the post-chat modal chain. Routes the user back to the
  // astrologers grid.
  //
  // Note (2026-08-05): The "Stay tuned to your stars" push-notification
  // opt-in modal used to fire here — it was irritating users after every
  // chat and is now disabled per product decision. If we ever want to
  // re-enable it, replace the body with `if (shouldShowPostChatPrompt())
  // setShowPushPrompt(true); else navigate("/astrologers");`. The
  // PostChatNotificationPrompt component + `showPushPrompt` state are
  // kept in place so re-enabling is a one-line change.
  const finalizeChatEnd = () => {
    navigate("/astrologers");
  };

  // Resume an ended chat — calls POST /chats with the same astrologer_id which
  // server-side detects the prior ended chat, reactivates it, and inserts a
  // "Welcome back" continuity marker. Same chat_id is preserved so the URL,
  // Claude's session memory, and the full message history stay intact.
  //
  // RAGE-TAP GUARD: previously this had NO loading state. On slow mobile
  // networks the POST took 2-4s, the user would tap Continue 5-6 times,
  // and every tap fired a fresh /chats POST → the astrologer's portal
  // got 5-6 consecutive ring notifications. Now: hard-disable the
  // button while in-flight AND for 1s after a successful resume so the
  // second tap is a no-op.
  const [resuming, setResuming] = useState(false);
  const resumeChat = async () => {
    if (!chat?.astrologer_id || resuming) return;
    setResuming(true);
    try {
      const { data: started } = await api.post("/chats", {
        astrologer_id: chat.astrologer_id,
        ad_session_id: getAdSessionId(),
      });
      toast.success("Picking up where you left off");
      // For HUMAN astrologers the backend creates a brand-NEW `ringing` chat
      // (resume-in-place is AI-only). The old ChatPage instance was bound to
      // the OLD chat_id, so refreshing it would forever show the paused
      // CTA. Navigate to the new chat_id so the page remounts with the
      // ringing → active state machine wired to the right document.
      const newChatId = started?.chat_id;
      if (newChatId && newChatId !== chatId) {
        // `replace: true` so the browser back-button doesn't bounce the
        // seeker back to the dead "paused" page they just escaped from.
        // Route is `/chat/:chatId` (singular) — NOT `/chats/:id`. The
        // plural form lands on MyChatsPage which renders blank when no
        // chatId param is in scope, surfacing as the "page goes blank"
        // bug after a successful resume.
        navigate(`/chat/${newChatId}`, { replace: true });
        return;
      }
      // AI astrologer resume-in-place — chat_id is the SAME, just refresh.
      const [{ data: refreshed }, { data: fresh }] = await Promise.all([
        api.get(`/chats/${chatId}`),
        api.get(`/chats/${chatId}/messages`),
      ]);
      setChat(refreshed);
      replaceAllMessages(fresh);
      try {
        const { data: b } = await api.get(`/chats/${chatId}/billing`);
        setBilling(b);
      } catch (_) {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resume — please try again.");
    } finally {
      // Keep the button locked for 1s after settlement so a panicked
      // second tap doesn't immediately fire another POST. The UI state
      // already reflects "active" by then, so the button isn't even
      // shown anymore in the happy path.
      setTimeout(() => setResuming(false), 1000);
    }
  };

  const submitReview = async () => {
    await api.post(`/chats/${chatId}/review`, { rating, comment });
    toast.success("Thank you for your feedback");
    setShowRating(false);
    // Customer feedback (Feb 2026): the VIP-King modal used to auto-pop
    // here and cover the CashbackCelebrationModal — seekers complained
    // that the VIP banner "bypassed" their reward claim. Now the VIP
    // offer is ONLY reachable from the explicit "Become VIP" CTA in the
    // site menu (see Layout.jsx). Here we just close the review and:
    //   • if a cashback celebration is still on screen, do nothing —
    //     the cashback modal owns navigation from here (its onRecharge
    //     opens the top-up; its onClose calls finalizeChatEnd),
    //   • otherwise, kick to the push prompt / astrologers grid.
    if (!cashbackCelebration) {
      finalizeChatEnd();
    }
  };

  if (!chat) return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col">
      <div className="hidden md:block"><Header /></div>
      <div className="flex-1 flex items-center justify-center text-muted text-sm">Loading conversation...</div>
    </div>
  );

  // Ringing — astrologer hasn't picked up yet. We used to render a
  // dedicated full-screen "waiting" screen with NO input — which made
  // multiple seekers report "I couldn't type at all". Now we render the
  // SAME chat UI as the active state, gated by a banner overlay at the
  // top + a placeholder hint. The send endpoint already accepts messages
  // for ringing chats (it only rejects status='ended'), so anything
  // typed lands in the conversation and the astrologer sees it the
  // moment they tap Accept. This eliminates the "stuck waiting" UX
  // failure mode entirely.
  const isRingingHuman = chat.status === "ringing";
  if (chat.status === "ended" && chat.declined) {
    return (
      <div className="bg-[#FAF4E8] md:bg-transparent min-h-[100dvh] flex flex-col" data-testid="chat-declined-screen">
        <div className="hidden md:block"><Header /></div>
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-5xl">🌙</p>
          <h2 className="font-serif-display text-2xl text-[#3F2D1B] mt-4">
            {astro?.name || "The astrologer"} is busy right now
          </h2>
          <p className="text-stone-700 text-sm mt-3 max-w-md">
            Please pick another astrologer or try again in a few minutes.
          </p>
          <button
            onClick={() => navigate("/astrologers")}
            className="btn-primary rounded-full px-6 py-2.5 text-sm mt-6"
            data-testid="declined-back-btn"
          >
            Back to astrologers
          </button>
        </div>
      </div>
    );
  }

  // Mobile-first chat layout: full-screen on phones, contained card on desktop.
  // On mobile we hide the global site header/footer for an "app-like" experience.
  return (
    <div className="bg-[#FAF4E8] md:bg-transparent min-h-[100dvh]" data-testid="chat-page">
      {/* Desktop-only site header */}
      <div className="hidden md:block">
        <Header />
      </div>

      <div className="md:max-w-4xl md:mx-auto md:px-6 lg:px-10 md:pt-6 md:pb-12">
        {/* Desktop-only back link (mobile uses back arrow in chat header) */}
        <button onClick={() => navigate("/dashboard")} className="hidden md:flex items-center gap-2 text-sm text-muted hover:text-gold transition-colors mb-4" data-testid="chat-back-btn">
          <ArrowLeft size={16} /> My chats
        </button>
        <div
          className="card-surface md:rounded-md overflow-hidden flex flex-col bg-[#FAF4E8] h-[100dvh] md:h-[calc(100vh-200px)]"
        >
          <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-soft bg-[#FAF4E8] sticky top-0 z-10 pt-[max(env(safe-area-inset-top),0.75rem)]">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <button
                onClick={() => navigate("/dashboard")}
                className="md:hidden p-1.5 -ml-1 rounded-full hover:bg-black/5 transition-colors flex-shrink-0"
                data-testid="chat-back-mobile-btn"
                aria-label="Back"
              >
                <ArrowLeft size={20} />
              </button>
              <img src={resolveAssetUrl(chat.astrologer_avatar)} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-serif-display text-base sm:text-xl truncate flex items-center gap-2">
                  <span className="truncate">{chat.astrologer_name}</span>
                  {user?.is_vip && <VipKingBadge variant="compact" />}
                </div>
                <div className="label-eyebrow !text-[10px] sm:!text-xs flex items-center gap-1.5 truncate">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
                  <span className="truncate">In session</span>
                  {billing?.rate_per_min !== undefined && (
                    <span className="text-muted normal-case tracking-normal truncate">· {formatPrice(billing.rate_per_min)}/min</span>
                  )}
                  {user?.is_vip && (
                    <VipKingBadge label="VIP" className="hidden sm:inline-flex ml-1" />
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Live time-left & wallet meter */}
              {liveElapsed && billing && (() => {
                const inFree = liveElapsed.seconds < (billing.free_minutes || 0) * 60;
                const isEnded = chat?.status === "ended" || billing?.status === "ended";
                // BILLING PAUSED — clock hasn't started yet. Show static
                // seconds_left (no local tick-down) and a friendly "paused"
                // micro-label so the user knows the meter is honestly frozen.
                // Also freeze when chat has ENDED so the wallet timer doesn't
                // appear to keep draining post-end.
                const isPaused = billing.billing_started === false || isEnded;
                const heartbeatTs = billing._heartbeat_ts || Date.now();
                const secsSinceHeartbeat = isPaused
                  ? 0
                  : Math.max(0, Math.floor((Date.now() - heartbeatTs) / 1000));
                const secsLeft = Math.max(0, (billing.seconds_left || 0) - secsSinceHeartbeat);
                const mmLeft = Math.floor(secsLeft / 60);
                const ssLeft = secsLeft % 60;
                const timeLeftLabel = `${String(mmLeft).padStart(2, "0")}:${String(ssLeft).padStart(2, "0")}`;
                const low = !isPaused && secsLeft <= 60;
                return (
                  <>
                    {/* Compact mobile time pill */}
                    <div
                      className={`sm:hidden flex items-center gap-1 px-2 py-1 rounded-full border ${
                        isPaused ? "border-slate-300 bg-slate-50 text-slate-600"
                          : inFree ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : low ? "border-rose-300 bg-rose-50 text-rose-700"
                          : "border-amber-300 bg-amber-50 text-amber-800"
                      }`}
                      data-testid="chat-time-left-mobile"
                    >
                      <span className="font-mono text-[11px] font-bold">{isPaused ? "⏸ " : ""}{timeLeftLabel}</span>
                    </div>
                    {/* Desktop full meters */}
                    <div className="text-right hidden sm:block" data-testid="chat-time-left">
                      <div className={`label-eyebrow !text-[10px] ${
                        isPaused ? "!text-slate-600"
                          : inFree ? "!text-emerald-700"
                          : low ? "!text-rose-700"
                          : "!text-amber-700"
                      }`}>
                        {isPaused ? "Paused" : inFree ? "Free + Paid time" : "Time left"}
                      </div>
                      <div className={`font-mono text-base font-bold ${
                        isPaused ? "text-slate-600"
                          : low ? "text-rose-600"
                          : "text-[#3F2D1B]"
                      }`}>{timeLeftLabel}</div>
                    </div>
                    <div className="text-right hidden sm:block" data-testid="chat-elapsed">
                      <div className="label-eyebrow !text-[10px]">Elapsed</div>
                      <div className="font-mono text-sm text-muted">{liveElapsed.label}</div>
                    </div>
                    <div className="text-right hidden sm:block" data-testid="chat-wallet-balance">
                      <div className="label-eyebrow !text-[10px]">Wallet</div>
                      <div className={`font-mono text-sm font-semibold ${(billing.wallet_balance ?? 0) < (billing.rate_per_min || 0) ? "text-rose-600" : "text-[#3F2D1B]"}`}>
                        {formatPrice(Number(billing.wallet_balance ?? 0))}
                      </div>
                    </div>
                  </>
                );
              })()}
              <button
                onClick={chat?.status === "ended" ? exitSession : endChat}
                className="btn-ghost rounded-full p-2 sm:px-4 sm:py-2 text-sm flex items-center gap-2"
                data-testid={chat?.status === "ended" ? "exit-chat-btn" : "end-chat-btn"}
                aria-label={chat?.status === "ended" ? "Close chat & rate" : "End chat"}
              >
                <X size={14} />
                <span className="hidden sm:inline">
                  {chat?.status === "ended" ? "Close" : "End chat"}
                </span>
              </button>
            </div>
          </div>

          {/* First-chat marketing banner — only shows during the user's
              very first chat (server-stamped via /chats/{id}). Disappears
              the moment the chat ends; the celebration modal takes over. */}
          {chat?.is_first_chat && chat?.status !== "ended" && (
            <div
              className="px-3 sm:px-6 py-2 bg-gradient-to-r from-[#FFB800] via-[#FFD66E] to-[#FFB800] text-[#1A1410] text-[11px] sm:text-xs font-semibold flex items-center justify-center gap-2 border-b border-amber-300 animate-pulse"
              data-testid="first-chat-prize-banner"
            >
              <span className="text-base">🎁</span>
              <span className="uppercase tracking-wider">
                You won a special prize · Opens when chat ends
              </span>
            </div>
          )}

          {/* Anniversary 50% campaign — gated by two flags:
              1. Global super-admin kill-switch at /admin/promos
                 (`anniversaryPromoOn === false` hides it everywhere).
              2. For HUMAN astrologer chats only: the user must NEVER
                 have paid for a Razorpay/PayPal wallet recharge (free
                 promo credits don't count). AI astrologer chats always
                 show when the kill-switch is ON. Banner morphs from
                 "Anniversary Gift..." (active) to "Congratulations!..."
                 (ended) and auto-applies ANNIVERSARY50 in the wallet
                 modal on CTA click. */}
          {anniversaryPromoOn && (astro?.is_ai || hasPaidRecharge === false) && (
          <div
            className={
              chat?.status === "ended"
                ? "px-3 sm:px-6 py-2.5 bg-gradient-to-r from-[#10B981] via-[#34D399] to-[#10B981] text-white text-xs sm:text-sm font-extrabold flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 border-b-2 border-emerald-700 shadow-lg"
                : "px-3 sm:px-5 py-2 bg-gradient-to-r from-[#FCD34D] via-[#FBBF24] to-[#FCD34D] text-[#422006] text-[11px] sm:text-sm font-bold flex items-center gap-2 border-b border-amber-500 shadow-sm leading-snug"
            }
            data-testid={chat?.status === "ended" ? "anniversary-cashback-banner" : "anniversary-promo-banner"}
          >
            <span className="flex items-start sm:items-center gap-2 min-w-0 flex-1">
              <span className="text-base sm:text-lg flex-shrink-0 leading-none">{chat?.status === "ended" ? "🎉" : "🎁"}</span>
              {chat?.status === "ended" ? (
                <span className="leading-tight drop-shadow-sm">
                  Congratulations! You won <span className="underline decoration-2 underline-offset-2">50% Cashback</span>
                </span>
              ) : (
                <span className="leading-snug">
                  <span className="font-extrabold">Anniversary Gift</span>
                  <span className="mx-1">&middot;</span>
                  You <span className="font-extrabold">WON</span> something special.{" "}
                  <span className="whitespace-nowrap">Complete Your Chat to Receive</span>
                </span>
              )}
            </span>
            {chat?.status === "ended" && (
              <button
                type="button"
                onClick={() => {
                  setTopupShortBy(100);
                  setTopupForZeroBalance(false);
                  setTopupPrefillCoupon("ANNIVERSARY50");
                  setTopupOpen(true);
                }}
                className="flex-shrink-0 rounded-full bg-white hover:bg-amber-50 active:bg-amber-100 text-emerald-700 px-4 py-1.5 text-xs uppercase tracking-wider font-extrabold transition shadow-md border border-emerald-300"
                data-testid="anniversary-recharge-btn"
                aria-label="Recharge to claim 50% cashback"
              >
                Claim Now
              </button>
            )}
          </div>
          )}
          {/* External-chart context banner — visible whenever a chart-other-
              than-yours is active. Lets the seeker switch back to their own
              chart in one click without restarting the chat. */}
          {chat?.external_chart?.kundali && chat.status !== "ended" && (
            <div className="px-3 sm:px-6 py-2 bg-violet-50 border-b border-violet-200 text-[11px] sm:text-xs text-violet-900 flex items-center justify-between gap-2 flex-wrap" data-testid="external-chart-banner">
              <span className="leading-snug flex items-center gap-1.5">
                <span className="text-base">🔮</span>
                <span>Reading <b>{chat.external_chart.birth_details?.name || "this person"}'s</b> chart · {chat.external_chart.birth_details?.date}{chat.external_chart.birth_details?.time ? ` · ${chat.external_chart.birth_details.time}` : ""} · {chat.external_chart.birth_details?.place}</span>
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.post(`/chats/${chatId}/external-chart/clear`);
                    const [{ data: refreshed }, { data: msgs }] = await Promise.all([
                      api.get(`/chats/${chatId}`),
                      api.get(`/chats/${chatId}/messages`),
                    ]);
                    setChat(refreshed);
                    replaceAllMessages(msgs);
                    toast.success("Back to your own chart");
                  } catch (err) {
                    toast.error("Could not switch back");
                  }
                }}
                className="text-violet-700 underline font-medium hover:text-violet-900"
                data-testid="external-chart-clear-btn"
              >
                Switch back to my chart →
              </button>
            </div>
          )}

          {/* Persistent banner under the header — shows total chat duration available right after chat starts */}
          {billing && billing.status !== "ended" && (
            <div className="px-3 sm:px-6 py-2 sm:py-2.5 bg-[#FFF6E0]/70 border-b border-amber-200 text-[11px] sm:text-xs text-[#3F2D1B] flex items-center justify-between gap-2 sm:gap-3 flex-wrap" data-testid="chat-duration-banner">
              <span className="leading-snug">
                {billing.billing_started === false ? (
                  <>⏸️ <b>Clock is paused</b> — your minutes start counting only when you send your first message ·</>
                ) : billing.free_minutes_remaining > 0 ? (
                  <>🎁 <b>{billing.free_minutes_remaining} min free</b> + {billing.affordable_paid_minutes} paid · </>
                ) : (
                  <>💬 <b>{billing.affordable_paid_minutes} min</b> @ {formatPrice(billing.rate_per_min)}/min · </>
                )}
                Total: <b>{billing.minutes_left} min</b>
                {/* Mobile-only inline wallet balance to keep it visible without a meter */}
                <span className="sm:hidden text-muted"> · Wallet {formatPrice(Number(billing.wallet_balance ?? 0))}</span>
              </span>
              {billing.minutes_left <= 3 && billing.free_minutes_remaining === 0 && billing.billing_started !== false && (
                <button
                  onClick={() => { setTopupShortBy(200); setTopupOpen(true); }}
                  className="text-gold underline font-medium hover:text-amber-700"
                  data-testid="banner-topup-cta"
                >
                  Top up →
                </button>
              )}
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6 space-y-3 overscroll-contain" data-testid="messages-list">
            {isRingingHuman && (
              <div
                className="rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 flex items-center gap-3 mb-2"
                data-testid="ringing-banner"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
                <div className="text-xs sm:text-sm leading-snug flex-1">
                  <span className="font-bold">Ringing {astro?.name?.split(" ")[0] || "the astrologer"}…</span>{" "}
                  Go ahead and type your question — they&apos;ll see it the second they pick up. Billing only starts once they reply.
                </div>
              </div>
            )}
            {messages.map((m) => (
              <MessageRenderer
                key={m.message_id}
                message={m}
                isRead={!!readStatus[m.message_id]}
              />
            ))}
            {/* Typing indicator — fires either when an AI/offline reply
                is expected (`isTyping`) OR when the live human astrologer
                is actively typing (`peerTyping`, polled every 2s). */}
            {(isTyping || peerTyping) && (
              <div className="flex justify-start" data-testid="typing-indicator">
                <div className="flex items-end gap-2">
                  {chat?.astrologer_avatar && (
                    <img
                      src={resolveAssetUrl(chat.astrologer_avatar)}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover flex-shrink-0 mb-0.5 opacity-80"
                    />
                  )}
                  <div className="bg-[#FAF4E8] border border-soft rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-1.5">
                    <span className="typing-dot" />
                    <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                    <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
                    <span className="text-[11px] text-muted ml-1.5 tracking-wide">
                      {chat?.astrologer_name?.split(" ")[0] || "Astrologer"} is typing
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <form
            onSubmit={send}
            className="border-t border-soft px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3 bg-[#FAF4E8]"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.625rem)" }}
          >
            {billing?.status === "ended" ? (
              <div className="flex-1 flex items-center justify-between gap-3 px-1" data-testid="chat-resume-bar">
                <span className="text-xs sm:text-sm text-muted leading-snug">
                  This consultation is paused. Pick up exactly where you left off.
                </span>
                <button
                  type="button"
                  onClick={resumeChat}
                  disabled={resuming}
                  className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="chat-resume-btn"
                >
                  {resuming ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Connecting…
                    </>
                  ) : (
                    <>
                      <Sparkle size={14} weight="fill" /> Continue
                    </>
                  )}
                </button>
              </div>
            ) : (
              <>
                {(astro?.specialization || "").toLowerCase() === "palmistry" && (
                  <PalmUploader
                    chatId={chat.chat_id}
                    question={text}
                    billingNotStarted={billing?.billing_started === false}
                  />
                )}
                <input
                  value={text}
                  onChange={(e) => { setText(e.target.value); if (e.target.value) pingTyping(); }}
                  placeholder={isRingingHuman ? `Type — ${astro?.name?.split(" ")[0] || "they"}'ll see this the moment they accept` : "Type your message..."}
                  disabled={sending}
                  enterKeyHint="send"
                  className="flex-1 bg-transparent outline-none px-3.5 py-2.5 text-base sm:text-sm border border-soft rounded-full focus:border-gold transition-colors disabled:opacity-50 min-w-0"
                  data-testid="chat-input"
                />
                <button type="submit" disabled={sending} className="btn-primary rounded-full w-11 h-11 flex items-center justify-center disabled:opacity-50 flex-shrink-0" data-testid="send-btn">
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </>
            )}
          </form>
          </div>
        </div>

        {showRating && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="rating-modal">
            <div className="card-surface rounded-md max-w-md w-full p-6 sm:p-8">
              <p className="label-eyebrow">Session ended</p>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light">How was your reading?</h3>
              <div className="flex gap-2 mt-6 justify-center">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} onClick={() => setRating(n)} className="p-2" data-testid={`star-${n}`}>
                    <Star size={32} weight={n <= rating ? "fill" : "regular"} className={n <= rating ? "text-gold" : "text-muted"} />
                  </button>
                ))}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share a few words (optional)"
                rows={3}
                className="w-full mt-5 px-4 py-3 border border-soft rounded-md text-sm focus:border-gold outline-none"
                data-testid="review-comment"
              />
              <button onClick={submitReview} className="btn-primary rounded-full w-full mt-5 py-3 font-medium" data-testid="submit-review-btn">
                Submit review
              </button>
            </div>
          </div>
        )}

        <WalletTopUp
          open={topupOpen}
          onClose={() => { setTopupOpen(false); setTopupForZeroBalance(false); setTopupPrefillCoupon(null); }}
          suggested={topupShortBy}
          prefilledCoupon={topupPrefillCoupon}
          headline={
            topupForZeroBalance && (billing?.seconds_left ?? 0) <= 0
              ? `Recharge to start chatting with ${chat?.astrologer_name?.split(" ")[0] || "your astrologer"}`
              : "Your free minutes are over"
          }
          subline={
            topupForZeroBalance && (billing?.seconds_left ?? 0) <= 0
              ? `Add a small balance to begin your reading. ${formatPrice(billing?.rate_per_min || astro?.rate_per_min || 0)}/min · pay via UPI, card, netbanking or wallet — secured by Razorpay.`
              : `Top up your wallet to continue the reading with ${chat?.astrologer_name}. Pay via UPI, card, netbanking or wallet — secured by Razorpay.`
          }
          onSuccess={() => { checkAuth?.(); }}
        />

        <VipKingOffer
          open={showVipOffer}
          onClose={() => { setShowVipOffer(false); finalizeChatEnd(); }}
          onSuccess={() => { checkAuth?.(); setShowVipOffer(false); finalizeChatEnd(); }}
          user={user}
        />

        <PostChatNotificationPrompt
          open={showPushPrompt}
          onClose={() => { setShowPushPrompt(false); navigate("/astrologers"); }}
        />

        {/* First-chat cashback celebration — fires once when the seeker
            ends their first paid chat. CTA opens the top-up modal so they
            can claim the +₹{X} bonus immediately.
            Post-chat modal chain (Feb 2026 fix): this modal is now the
            LAST step for reward-eligible seekers. When it closes (via
            'Maybe later' or after top-up completes), we run
            finalizeChatEnd so the seeker is routed to the astrologers
            grid — replacing the old VIP-banner auto-pop that used to
            cover this modal and steal the claim interaction. */}
        <CashbackCelebrationModal
          open={!!cashbackCelebration}
          amount={cashbackCelebration?.amount || 50}
          onRecharge={() => {
            const amt = cashbackCelebration?.amount || 50;
            setCashbackCelebration(null);
            // Pre-fill the top-up modal with ₹100 — the example math in
            // the celebration modal references that amount.
            setTopupShortBy(100);
            setTopupForZeroBalance(false);
            setTopupOpen(true);
            void amt;
          }}
          onClose={() => {
            setCashbackCelebration(null);
            // Chat is ended and review is not open → this was the final
            // modal in the post-chat sequence. Route the seeker onwards
            // so they don't get stuck on the finished chat surface.
            if (chat?.status === "ended" && !showRating) {
              finalizeChatEnd();
            }
          }}
        />

        {/* Whose-chart picker — first impression for AI astrologer chats */}
        {showChartPicker && !showExternalForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm" data-testid="chart-picker-modal">
            <div className="card-surface rounded-2xl max-w-md w-full p-6 sm:p-8 text-center">
              <Sparkle size={32} weight="duotone" className="text-amber-500 mx-auto" />
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light leading-tight">Whose chart shall we read?</h3>
              <p className="text-sm text-muted mt-2">
                Choose whose life you'd like {astro?.name?.split(" ").slice(-1)[0] || "the astrologer"} to look at today.
                {chat?.external_chart?.kundali && (
                  <span className="block text-[11px] text-violet-700 mt-1.5">
                    Currently reading <b>{chat.external_chart.birth_details?.name}'s</b> chart.
                  </span>
                )}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                <button
                  type="button"
                  onClick={pickMyOwnChart}
                  className="rounded-xl border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 p-4 text-left transition-all"
                  data-testid="chart-picker-mine"
                >
                  <div className="text-2xl">🪔</div>
                  <div className="font-medium text-[#3F2D1B] mt-2">My own chart</div>
                  <div className="text-[11px] text-muted mt-1">Use the birth details on my profile.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setShowExternalForm(true)}
                  className="rounded-xl border-2 border-soft hover:border-amber-300 hover:bg-amber-50 p-4 text-left transition-all"
                  data-testid="chart-picker-other"
                >
                  <div className="text-2xl">🔮</div>
                  <div className="font-medium text-[#3F2D1B] mt-2">Someone else's chart</div>
                  <div className="text-[11px] text-muted mt-1">Family, partner, friend — anyone's chart you'd like read.</div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* External-chart form */}
        {showExternalForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm" data-testid="external-chart-modal">
            <div className="card-surface rounded-2xl max-w-md w-full p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="label-eyebrow !text-amber-700 !text-[10px]">Reading someone else's chart</p>
                  <h3 className="font-serif-display text-xl sm:text-2xl mt-2 font-light leading-tight">Their birth details</h3>
                </div>
                <button
                  onClick={() => { setShowExternalForm(false); setShowChartPicker(true); }}
                  className="text-muted hover:text-[#3F2D1B] p-1"
                  data-testid="external-back-btn"
                  aria-label="Back"
                >
                  <ArrowLeft size={18} />
                </button>
              </div>
              <p className="text-xs text-muted mt-2 leading-relaxed">
                All four are required to compute an accurate kundali. Use the place where they were physically born.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#3F2D1B] mb-1.5" htmlFor="ext-name">
                    Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="ext-name"
                    type="text"
                    value={extForm.name}
                    onChange={(e) => setExtForm({ ...extForm, name: e.target.value })}
                    placeholder="e.g., Riya, Dad, Partner"
                    className="w-full px-4 py-2.5 border border-soft rounded-lg text-sm focus:border-gold outline-none"
                    data-testid="external-name"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3F2D1B] mb-1.5" htmlFor="ext-date">
                    Date of birth <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="ext-date"
                    type="date"
                    value={extForm.date}
                    onChange={(e) => setExtForm({ ...extForm, date: e.target.value })}
                    className="w-full px-4 py-2.5 border border-soft rounded-lg text-sm focus:border-gold outline-none"
                    data-testid="external-date"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3F2D1B] mb-1.5">
                    Time of birth <span className="text-rose-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      value={(() => {
                        if (!extForm.time) return "";
                        const [h] = extForm.time.split(":");
                        const hn = parseInt(h, 10);
                        const h12 = ((hn + 11) % 12) + 1;
                        return String(h12).padStart(2, "0");
                      })()}
                      onChange={(e) => {
                        const h12 = parseInt(e.target.value, 10);
                        const m = extForm.time ? extForm.time.split(":")[1] || "00" : "00";
                        const isPm = extForm.time ? parseInt(extForm.time.split(":")[0], 10) >= 12 : false;
                        const h24 = h12 === 12 ? (isPm ? 12 : 0) : (isPm ? h12 + 12 : h12);
                        setExtForm({ ...extForm, time: `${String(h24).padStart(2, "0")}:${m}` });
                      }}
                      className="w-full px-3 py-2.5 border border-soft rounded-lg text-sm focus:border-gold outline-none bg-white"
                      data-testid="external-time-hour"
                    >
                      <option value="">HH</option>
                      {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <select
                      value={extForm.time ? (extForm.time.split(":")[1] || "00") : ""}
                      onChange={(e) => {
                        const m = e.target.value;
                        const h = extForm.time ? extForm.time.split(":")[0] || "00" : "00";
                        setExtForm({ ...extForm, time: `${h}:${m}` });
                      }}
                      className="w-full px-3 py-2.5 border border-soft rounded-lg text-sm focus:border-gold outline-none bg-white"
                      data-testid="external-time-minute"
                    >
                      <option value="">MM</option>
                      {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <select
                      value={extForm.time ? (parseInt(extForm.time.split(":")[0], 10) >= 12 ? "PM" : "AM") : ""}
                      onChange={(e) => {
                        if (!extForm.time) {
                          // Default to 12:00 in chosen meridian if nothing picked yet
                          setExtForm({ ...extForm, time: e.target.value === "PM" ? "12:00" : "00:00" });
                          return;
                        }
                        const [h, m] = extForm.time.split(":");
                        let hn = parseInt(h, 10);
                        const wasPm = hn >= 12;
                        const wantPm = e.target.value === "PM";
                        if (wasPm && !wantPm) hn = hn === 12 ? 0 : hn - 12;
                        if (!wasPm && wantPm) hn = hn === 0 ? 12 : hn + 12;
                        setExtForm({ ...extForm, time: `${String(hn).padStart(2, "0")}:${m || "00"}` });
                      }}
                      className="w-full px-3 py-2.5 border border-soft rounded-lg text-sm focus:border-gold outline-none bg-white"
                      data-testid="external-time-meridian"
                    >
                      <option value="">--</option>
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  <p className="text-[10px] text-muted mt-1">
                    Use the exact time noted on their birth certificate.
                    {extForm.time && (() => {
                      const [h, m] = extForm.time.split(":");
                      const hn = parseInt(h, 10);
                      const h12 = ((hn + 11) % 12) + 1;
                      const ampm = hn >= 12 ? "PM" : "AM";
                      return (
                        <span className="text-emerald-700 ml-1.5">
                          ✓ Selected: {String(h12).padStart(2, "0")}:{m} {ampm}
                        </span>
                      );
                    })()}
                  </p>
                </div>
                <div className="relative">
                  <label className="block text-xs font-medium text-[#3F2D1B] mb-1.5" htmlFor="ext-place">
                    Place of birth <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="ext-place"
                    type="text"
                    value={extForm.place}
                    onChange={async (e) => {
                      const q = e.target.value;
                      setExtForm({ ...extForm, place: q, timezone: "" });
                      if (q.trim().length >= 2) {
                        try {
                          const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                          setExtPlaceSuggestions(sug || []);
                        } catch { /* ignore */ }
                      } else {
                        setExtPlaceSuggestions([]);
                      }
                    }}
                    placeholder="Start typing — Mumbai, Delhi, Pretoria..."
                    className="w-full px-4 py-2.5 border border-soft rounded-lg text-sm focus:border-gold outline-none"
                    data-testid="external-place"
                    autoComplete="off"
                  />
                  {extPlaceSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-56 overflow-y-auto card-surface rounded-lg border border-soft z-30 shadow-lg">
                      {extPlaceSuggestions.map((s, i) => (
                        <button
                          type="button"
                          key={i}
                          onClick={() => {
                            const label = s.name || s.label;
                            setExtForm({
                              ...extForm,
                              place: label,
                              timezone: s.timezone || "Asia/Kolkata",
                            });
                            setExtPlaceSuggestions([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                          data-testid={`external-place-option-${i}`}
                        >
                          {s.name || s.label}
                          {s.timezone && (
                            <span className="text-[10px] text-muted ml-2">· {s.timezone}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {extForm.timezone ? (
                    <p className="text-[10px] text-emerald-700 mt-1.5">
                      ✓ Timezone resolved: {extForm.timezone}
                    </p>
                  ) : (
                    <p className="text-[10px] text-muted mt-1">Pick the city from the dropdown so we lock the correct timezone.</p>
                  )}
                </div>
              </div>
              <button
                onClick={submitExternalChart}
                disabled={extSubmitting}
                className="btn-primary rounded-full w-full mt-5 py-3 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="external-submit-btn"
              >
                {extSubmitting ? "Computing chart..." : "Show me their chart"}
              </button>
              <p className="text-[10px] text-muted text-center mt-3 leading-relaxed">
                You can switch back to your own chart anytime from the header.
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Desktop-only footer */}
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}

/**
 * Seeker-side ringing screen with a 90-second countdown that mirrors
 * the server-side auto-decline TTL on /api/chats/{id}. Stays in sync
 * with the parent's polling so when the server flips status the
 * regular chat UI takes over.
 */
function RingingScreen({ astro, chat, navigate }) {
  const [secondsLeft, setSecondsLeft] = useState(90);
  useEffect(() => {
    if (!chat?.started_at) return;
    const start = new Date(chat.started_at).getTime();
    const tick = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setSecondsLeft(Math.max(0, 90 - elapsed));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [chat?.started_at]);

  return (
    <div className="bg-[#FAF4E8] md:bg-transparent min-h-[100dvh] flex flex-col" data-testid="chat-ringing-screen">
      <div className="hidden md:block"><Header /></div>
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        {astro?.avatar && (
          <img
            src={astro.avatar}
            alt=""
            className="w-28 h-28 rounded-full object-cover border-4 border-amber-300 shadow-xl animate-pulse"
          />
        )}
        <p className="label-eyebrow mt-6">Connecting</p>
        <h2 className="font-serif-display text-3xl text-[#3F2D1B] mt-2">
          {astro?.name || "Astrologer"} is being notified
        </h2>
        <p className="text-stone-700 text-sm mt-3 max-w-md leading-relaxed">
          We&rsquo;re ringing them now. As soon as they pick up, your chat opens and the timer starts.
        </p>
        <div className="flex items-center gap-1.5 mt-6 text-amber-700 text-xs uppercase tracking-widest font-semibold">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
          </span>
          Ringing &middot; <span data-testid="ringing-countdown" className="font-mono normal-case tracking-normal">{secondsLeft}s</span>
        </div>
        <button
          onClick={() => navigate("/astrologers")}
          className="mt-10 px-6 py-2.5 rounded-full text-sm border border-stone-300 text-stone-600 hover:bg-stone-100 transition"
          data-testid="ringing-cancel-btn"
        >
          Try someone else
        </button>
      </div>
    </div>
  );
}

